import { useState } from "react";
import { RefreshCw, XCircle, Ban, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—");
const TONE = { active: "bg-emerald-100 text-emerald-800", trialing: "bg-emerald-100 text-emerald-800", past_due: "bg-amber-100 text-amber-800", canceled: "bg-slate-200 text-slate-700" };

export function OwnerSubscriptionCard({ targetType, targetId, subscription, onChanged }) {
  const [busy, setBusy] = useState("");
  const sub = subscription;
  const run = async (key, fn, ok) => {
    setBusy(key);
    try {
      await fn();
      toast.success(ok);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Request failed");
    } finally {
      setBusy("");
    }
  };
  const base = `/platform/${targetType}/${targetId}/subscription`;
  const cancelEnd = () => window.confirm("Cancel at period end? The client keeps access until their paid date, then no further charges.") &&
    run("end", () => api.post(`${base}/cancel`, { immediately: false }), "Subscription will end at period end.");
  const cancelNow = () => window.confirm("Cancel IMMEDIATELY? Stripe stops the subscription now; the client keeps access only until their current paid-until date.") &&
    run("now", () => api.post(`${base}/cancel`, { immediately: true }), "Subscription canceled.");
  const resume = () => run("resume", () => api.post(`${base}/resume`), "Auto-renewal resumed.");

  const Btn = ({ k, onClick, children, tone }) => (
    <button type="button" onClick={onClick} disabled={!!busy} data-testid={`owner-subscription-${k}-btn`}
      className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold transition disabled:opacity-60 ${tone}`}>
      {busy === k ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : children}
    </button>
  );

  return (
    <section className="rounded-xl border border-slate-200 bg-white px-5 py-4 mb-6 flex flex-wrap items-center justify-between gap-4" data-testid="owner-subscription-card">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Stripe subscription</div>
        {sub?.id ? (
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-700">
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${TONE[sub.status] || "bg-slate-100 text-slate-700"}`} data-testid="owner-subscription-status">
              {sub.status.replace("_", " ")}
            </span>
            <span className="capitalize">{sub.plan} plan</span>
            <span className="text-slate-400">·</span>
            <span>{sub.cancel_at_period_end ? `Ends ${fmt(sub.current_period_end)}` : sub.status === "canceled" ? "No further charges" : `Next charge ${fmt(sub.current_period_end)}`}</span>
            {sub.canceled_by && <span className="text-xs text-slate-500">(canceled by {sub.canceled_by.replace("_", " ")})</span>}
          </div>
        ) : (
          <div className="mt-1 text-sm text-slate-500" data-testid="owner-subscription-none">No auto-renewing subscription — this account pays manually.</div>
        )}
      </div>
      {sub?.active && (
        <div className="flex flex-wrap gap-2">
          {sub.cancel_at_period_end
            ? <Btn k="resume" onClick={resume} tone="border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"><RefreshCw className="h-3.5 w-3.5" /> Resume</Btn>
            : <Btn k="end" onClick={cancelEnd} tone="border-slate-200 bg-white text-slate-800 hover:bg-slate-50"><XCircle className="h-3.5 w-3.5" /> Cancel at period end</Btn>}
          <Btn k="now" onClick={cancelNow} tone="border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"><Ban className="h-3.5 w-3.5" /> Cancel immediately</Btn>
        </div>
      )}
    </section>
  );
}
